export const MAX_FILE_MB = 100

const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024
const MIN_IMAGE_SIDE = 8
const MAX_IMAGES = 200

export interface ExtractImagesProgress {
  current: number
  total: number
}

export interface ExtractedPdfImage {
  blob: Blob
  filename: string
  page: number
  width: number
  height: number
}

export interface ExtractImagesResult {
  images: ExtractedPdfImage[]
  pageCount: number
  totalBytes: number
  truncated: boolean
}

export interface ExtractImagesOptions {
  file: File
  signal: AbortSignal
  onProgress: (progress: ExtractImagesProgress) => void
}

interface PdfJsModule {
  GlobalWorkerOptions: { workerSrc: string }
  OPS: Record<string, number>
  getDocument: (src: { data: ArrayBuffer }) => { promise: Promise<PdfJsDocument> }
}

interface PdfJsDocument {
  numPages: number
  getPage: (pageNumber: number) => Promise<PdfJsPage>
  destroy: () => Promise<void>
}

interface PdfJsPage {
  getOperatorList: () => Promise<PdfOperatorList>
  objs: PdfObjectStore
  commonObjs?: PdfObjectStore
  cleanup: () => void
}

interface PdfOperatorList {
  fnArray: number[]
  argsArray: unknown[][]
}

interface PdfObjectStore {
  get: (id: string, callback?: (data: unknown) => void) => unknown
}

interface PdfImageData {
  width: number
  height: number
  data: ArrayLike<number>
}

type DrawableImageSource =
  | HTMLImageElement
  | HTMLVideoElement
  | HTMLCanvasElement
  | ImageBitmap
  | OffscreenCanvas

export function validateFileSize(file: File): boolean {
  return file.size <= MAX_FILE_BYTES
}

export function imageZipFilename(originalName: string): string {
  return `${baseName(originalName)}-images.zip`
}

function imageFilename(originalName: string, index: number, total: number): string {
  const width = Math.max(2, String(total).length)
  return `${baseName(originalName)}-image-${String(index).padStart(width, '0')}.png`
}

function baseName(originalName: string): string {
  const dot = originalName.lastIndexOf('.')
  return dot === -1 ? originalName : originalName.slice(0, dot)
}

function assertNotAborted(signal: AbortSignal) {
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError')
}

function isPdfImageData(value: unknown): value is PdfImageData {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as { width?: unknown; height?: unknown; data?: unknown }
  return (
    typeof candidate.width === 'number' &&
    typeof candidate.height === 'number' &&
    candidate.data !== undefined &&
    typeof (candidate.data as { length?: unknown }).length === 'number'
  )
}

function isCanvasImageSource(value: unknown): value is DrawableImageSource {
  return (
    value instanceof HTMLImageElement ||
    value instanceof HTMLVideoElement ||
    value instanceof HTMLCanvasElement ||
    (typeof ImageBitmap !== 'undefined' && value instanceof ImageBitmap) ||
    (typeof OffscreenCanvas !== 'undefined' && value instanceof OffscreenCanvas)
  )
}

function getSourceSize(source: DrawableImageSource): { width: number; height: number } {
  if (source instanceof HTMLImageElement) {
    return { width: source.naturalWidth, height: source.naturalHeight }
  }
  if (source instanceof HTMLVideoElement) {
    return { width: source.videoWidth, height: source.videoHeight }
  }
  return { width: source.width, height: source.height }
}

function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error('encode-failed'))
    }, 'image/png')
  })
}

async function imageDataToPng(image: PdfImageData): Promise<Blob | null> {
  const width = Math.floor(image.width)
  const height = Math.floor(image.height)
  if (width < MIN_IMAGE_SIDE || height < MIN_IMAGE_SIDE) return null

  const source = image.data
  const pixelCount = width * height
  const rgba = new Uint8ClampedArray(pixelCount * 4)

  if (source.length >= pixelCount * 4) {
    for (let i = 0; i < pixelCount * 4; i++) rgba[i] = source[i] ?? 0
  } else if (source.length >= pixelCount * 3) {
    for (let pixel = 0; pixel < pixelCount; pixel++) {
      const sourceOffset = pixel * 3
      const targetOffset = pixel * 4
      rgba[targetOffset] = source[sourceOffset] ?? 0
      rgba[targetOffset + 1] = source[sourceOffset + 1] ?? 0
      rgba[targetOffset + 2] = source[sourceOffset + 2] ?? 0
      rgba[targetOffset + 3] = 255
    }
  } else if (source.length >= pixelCount) {
    for (let pixel = 0; pixel < pixelCount; pixel++) {
      const value = source[pixel] ?? 0
      const targetOffset = pixel * 4
      rgba[targetOffset] = value
      rgba[targetOffset + 1] = value
      rgba[targetOffset + 2] = value
      rgba[targetOffset + 3] = 255
    }
  } else {
    return null
  }

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas-unavailable')
  ctx.putImageData(new ImageData(rgba, width, height), 0, 0)
  const blob = await canvasToPngBlob(canvas)
  canvas.width = 0
  canvas.height = 0
  return blob
}

async function canvasSourceToPng(source: DrawableImageSource): Promise<Blob | null> {
  const { width, height } = getSourceSize(source)
  if (width < MIN_IMAGE_SIDE || height < MIN_IMAGE_SIDE) return null

  const canvas = document.createElement('canvas')
  canvas.width = Math.floor(width)
  canvas.height = Math.floor(height)
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas-unavailable')
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height)
  const blob = await canvasToPngBlob(canvas)
  canvas.width = 0
  canvas.height = 0
  return blob
}

async function toPngBlob(value: unknown): Promise<{ blob: Blob; width: number; height: number } | null> {
  if (isPdfImageData(value)) {
    const blob = await imageDataToPng(value)
    if (!blob) return null
    return { blob, width: Math.floor(value.width), height: Math.floor(value.height) }
  }

  if (isCanvasImageSource(value)) {
    const { width, height } = getSourceSize(value)
    const blob = await canvasSourceToPng(value)
    if (!blob) return null
    return { blob, width: Math.floor(width), height: Math.floor(height) }
  }

  return null
}

function getPdfObject(store: PdfObjectStore, id: string): Promise<unknown> {
  return new Promise((resolve, reject) => {
    try {
      const immediate = store.get(id, (data) => resolve(data))
      if (immediate !== undefined) resolve(immediate)
    } catch (error) {
      reject(error)
    }
  })
}

function imageObjectIdFor(fn: number, args: unknown[], ops: Record<string, number>): string | null {
  const imageOps = new Set([
    ops.paintImageXObject,
    ops.paintJpegXObject,
    ops.paintImageXObjectRepeat,
    ops.paintJpegXObjectRepeat,
  ])
  if (!imageOps.has(fn)) return null
  const firstArg = args[0]
  return typeof firstArg === 'string' ? firstArg : null
}

function inlineImageFor(fn: number, args: unknown[], ops: Record<string, number>): unknown | null {
  if (fn !== ops.paintInlineImageXObject) return null
  return args[0] ?? null
}

async function resolveImageObject(page: PdfJsPage, objectId: string): Promise<unknown> {
  try {
    return await getPdfObject(page.objs, objectId)
  } catch {
    if (page.commonObjs) return getPdfObject(page.commonObjs, objectId)
    throw new Error('image-object-unavailable')
  }
}

export async function extractImagesFromPdf(options: ExtractImagesOptions): Promise<ExtractImagesResult> {
  const { file, signal, onProgress } = options

  assertNotAborted(signal)

  const pdfjs = (await import('pdfjs-dist')) as unknown as PdfJsModule
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl

  const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise

  try {
    const images: ExtractedPdfImage[] = []
    const seenObjectIds = new Set<string>()
    const pageCount = doc.numPages
    let totalBytes = 0
    let truncated = false

    onProgress({ current: 0, total: pageCount })

    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
      assertNotAborted(signal)

      const page = await doc.getPage(pageNumber)
      const operatorList = await page.getOperatorList()

      for (let i = 0; i < operatorList.fnArray.length; i++) {
        if (images.length >= MAX_IMAGES) {
          truncated = true
          break
        }

        const fn = operatorList.fnArray[i]
        if (fn === undefined) continue
        const args = operatorList.argsArray[i] ?? []
        const objectId = imageObjectIdFor(fn, args, pdfjs.OPS)
        const source = objectId ? await resolveImageObject(page, objectId) : inlineImageFor(fn, args, pdfjs.OPS)
        if (!source) continue

        const dedupeKey = objectId ? `${pageNumber}:${objectId}` : `${pageNumber}:inline:${i}`
        if (seenObjectIds.has(dedupeKey)) continue
        seenObjectIds.add(dedupeKey)

        const image = await toPngBlob(source)
        if (!image) continue

        const filename = imageFilename(file.name, images.length + 1, MAX_IMAGES)
        images.push({
          blob: image.blob,
          filename,
          page: pageNumber,
          width: image.width,
          height: image.height,
        })
        totalBytes += image.blob.size
      }

      page.cleanup()
      onProgress({ current: pageNumber, total: pageCount })

      if (truncated) break
    }

    const total = images.length
    return {
      images: images.map((image, index) => ({
        ...image,
        filename: imageFilename(file.name, index + 1, total),
      })),
      pageCount,
      totalBytes,
      truncated,
    }
  } finally {
    await doc.destroy().catch(() => {})
  }
}

export async function packImagesZip(images: ExtractedPdfImage[]): Promise<Blob> {
  const JSZip = (await import('jszip')).default
  const zip = new JSZip()
  images.forEach((image) => {
    zip.file(image.filename, image.blob, { compression: 'STORE' })
  })
  return zip.generateAsync({ type: 'blob', compression: 'STORE' })
}
